/**
 * Shared types, option lists and small pieces for the PO / SO Headers filter
 * panel. The panel is split by view (customer vs supplier) so each file stays
 * under the 500-line limit; both views read from here.
 */

import { PO_HEADER_STATUS_LABELS } from '../../poHeaderStatus';

/** Horizontal divider with padding so it doesn't touch card edges */
export const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

export interface FilterOption { value: string; label: string }

/**
 * The header statuses a user can filter by — the LIVE three, from the one owner.
 *
 * CR-7412: this was a hand-copy of the header vocabulary, and the sweep that gave the
 * header ONE owner (`poHeaderStatus.ts`) missed it. It offered the pre-CR words —
 * "Sent", "Draft", "Acknowledged", "Delivered" — beside a grid that badges the same row
 * "Open", so you filtered by Sent and got rows labelled Open; and three of the six
 * matched zero rows on any tenant, so they were dead controls.
 *
 * Landed here rather than in POHeaderFilterSections because this file is where the
 * upstream refactor moved the list, and BOTH views read it — the customer panel and the
 * supplier one. Fixing it at its old address would have fixed one surface of two.
 *
 * Only the live keys are offered. The retired three keep LABELS in
 * `PO_HEADER_STATUS_LABELS` so a stray row still renders a word, but there is no reason
 * to offer a filter for a value nothing writes.
 */
const LIVE_HEADER_STATUSES = ['sent', 'in_progress', 'closed'] as const;
export const STATUS_OPTIONS: FilterOption[] = LIVE_HEADER_STATUSES.map((value) => ({
  value,
  label: PO_HEADER_STATUS_LABELS[value],
}));

/** CR-6679: Header-level acknowledgement status options. */
export const ACK_STATUS_OPTIONS: FilterOption[] = [
  { value: 'fully_acknowledged', label: 'Fully Acknowledged' },
  { value: 'partially_acknowledged', label: 'Partially Acknowledged' },
  { value: 'not_acknowledged', label: 'Not Acknowledged' },
  { value: 'no_open_lines', label: 'No Open Lines' },
];

/**
 * CR-7388: the Ack Status a deep link asks for (`?ack_status_summary=`), or
 * null when it names none of the filter's options. The Classic Procurement
 * Summary's "PO Headers Not Acknowledged" link sets it to not_acknowledged.
 *
 * @param value - the raw URL parameter
 * @returns the option value, or null
 */
export function ackStatusFromUrl(value: string | null): string | null {
  return value && ACK_STATUS_OPTIONS.some((o) => o.value === value) ? value : null;
}

/** The header panel's filter state, as owned by pages/Purchasing.tsx. */
export interface POHeaderSectionFilters {
  poNumber: string;
  supplier: string;
  customer: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  site: string;
  /** CR-6446: Warehouse filter for Site → Warehouse cascade. */
  warehouse: string;
  division: string;
  companyCountry: string;
  supplierGroup: string;
  supplierCountry: string;
  /** CR-6439 rework: Supplier Code (contains match). */
  supplierCode: string;
  /** CR-6439 rework: Supplier Type — '' | 'internal' | 'external'. */
  supplierType: string;
  commodityGroup: string;
  commodityType: string;
  /** CR-6439 rework: Order Type — raw ERP order-type code. */
  orderType: string;
  pdfAttached: string;
  strategic: string;
  ackStatus: string;  // CR-6679: Header-level acknowledgement status filter
}

export interface POHeaderTextInputs { poNumber: string; supplier: string; customer: string }

/** Cascade change handlers from usePOHeaderFilterCascades. */
export interface POHeaderCascadeHandlers {
  handleDivisionChange: (value: string) => void;
  handleCompanyCountryChange: (value: string) => void;
  handleSiteChange: (value: string) => void;
  handleSupplierCountryChange: (value: string) => void;
  handleSupplierGroupChange: (value: string) => void;
  handleCommodityGroupChange: (value: string) => void;
}

/**
 * What each view panel (customer / supplier) receives from the entry
 * component: the already-cascaded option lists plus the change handlers.
 */
export interface POHeaderPanelProps {
  filters: POHeaderSectionFilters;
  textInputs: POHeaderTextInputs;
  siteOptions: FilterOption[];
  warehouseOptions: FilterOption[];
  divisionOptions: FilterOption[];
  siteCountries: string[];
  supplierCountries: string[];
  supplierGroupOptions: FilterOption[];
  supplierSiteOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  orderTypeOptions: FilterOption[];
  handlers: POHeaderCascadeHandlers;
  onFilterChange: (key: string, value: string) => void;
  /** Called when Enter is pressed on text inputs to apply filters. */
  onApply?: () => void;
}
